/**
 * Copyright (c) 2025 Tj L Collins, DaddySavesTheWorld, and Mythbound Adventure Studio
 * All Rights Reserved
 *
 * @fileoverview Discord bridge awareness and help component with floating widget
 * @version 2.0.0
 * @since v0.0.07
 *
 * PURPOSE:
 * Document that web voice today is web-only unless backend relays
 * into Discord; ensure UI labels clarify who can hear whom.
 * Now includes floating widget with minimize and pin capabilities.
 *
 * Per FRONTEND_TODO.MD (Discord Bridge Awareness):
 * - Inline help text/tooltips in voice UI
 * - Doc in onboarding/help
 * - Clarify audience reach once relay is built
 *
 * Per Agent_A_Voice_Bridge.md:
 * - Floating/Fixed position in bottom-right
 * - Minimize to tab with icons + participant count
 * - Pin to sidebar option
 * - Codespace for future popout window
 *
 * CHANGE LOG:
 * 2025-01-07 - Initial implementation v1.0.0
 * 2026-01-05 - Agent A - Added floating widget, minimize, pin features v2.0.0
 */

import React, { useState, useEffect } from 'react';
// Added 2026-02-23: import formatMessage for audience label template interpolation
import { voiceBridge, formatMessage } from '../../i18n/voiceMessages';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Bridge status types
 */
export type BridgeStatus = 'disconnected' | 'connecting' | 'connected' | 'relay_pending';

/**
 * Position mode for the widget
 */
export type WidgetPosition = 'floating' | 'inline' | 'sidebar';

export interface VoiceBridgeInfoProps {
  /** Current bridge status */
  bridgeStatus?: BridgeStatus;
  /** Whether Discord bridge relay is available */
  bridgeRelayEnabled?: boolean;
  /** Number of Discord users who can hear */
  discordListeners?: number;
  /** Number of web users who can hear */
  webListeners?: number;
  /** Show as tooltip, inline, modal, or floating widget */
  variant?: 'tooltip' | 'inline' | 'modal' | 'floating';
  /** Position for tooltip */
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  /** Optional CSS class name */
  className?: string;
  /** Children to wrap with tooltip */
  children?: React.ReactNode;
  /** Whether mic is muted (for minimized icon display) */
  isMuted?: boolean;
  /** Callback when pin state changes */
  onPinChange?: (isPinned: boolean) => void;
  /** Initial pinned state */
  initialPinned?: boolean;
  /** Initial minimized state */
  initialMinimized?: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const LOCALSTORAGE_KEY_PINNED = 'voiceBridgeInfo_pinned';
const LOCALSTORAGE_KEY_MINIMIZED = 'voiceBridgeInfo_minimized';

/**
 * Content explaining bridge functionality
 */
const BRIDGE_HELP_CONTENT = {
  title: voiceBridge.help.title,
  sections: [
    {
      heading: voiceBridge.help.sections.whoCanHear.heading,
      icon: '👥',
      content: voiceBridge.help.sections.whoCanHear.content
    },
    {
      heading: voiceBridge.help.sections.discord.heading,
      icon: '🎮',
      content: voiceBridge.help.sections.discord.content
    },
    {
      heading: voiceBridge.help.sections.modes.heading,
      icon: '🎙️',
      content: voiceBridge.help.sections.modes.content
    },
    {
      heading: voiceBridge.help.sections.status.heading,
      icon: '📡',
      content: voiceBridge.help.sections.status.content
    }
  ]
};

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * VoiceBridgeInfo Component
 *
 * Provides awareness about Discord bridge functionality:
 * - Explains who can hear whom
 * - Shows current bridge status
 * - Clarifies audience reach
 *
 * Can render as tooltip, inline info, modal, or floating widget.
 *
 * Floating widget features:
 * - Fixed position in bottom-right corner
 * - Minimize to compact tab with icons + count
 * - Pin to sidebar (renders in sidebar portal)
 * - Codespace for future popout window
 */
export const VoiceBridgeInfo: React.FC<VoiceBridgeInfoProps> = ({
  bridgeStatus = 'relay_pending',
  bridgeRelayEnabled = false,
  discordListeners = 0,
  webListeners = 0,
  variant = 'tooltip',
  tooltipPosition = 'bottom',
  className = '',
  children,
  isMuted = false,
  onPinChange,
  initialPinned,
  initialMinimized
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Floating widget state
  const [isMinimized, setIsMinimized] = useState(() => {
    if (initialMinimized !== undefined) return initialMinimized;
    const saved = localStorage.getItem(LOCALSTORAGE_KEY_MINIMIZED);
    return saved === 'true';
  });
  
  const [isPinned, setIsPinned] = useState(() => {
    if (initialPinned !== undefined) return initialPinned;
    const saved = localStorage.getItem(LOCALSTORAGE_KEY_PINNED);
    return saved === 'true';
  });

  // Persist minimize/pin state
  useEffect(() => {
    localStorage.setItem(LOCALSTORAGE_KEY_MINIMIZED, String(isMinimized));
  }, [isMinimized]);

  useEffect(() => {
    localStorage.setItem(LOCALSTORAGE_KEY_PINNED, String(isPinned));
    onPinChange?.(isPinned);
  }, [isPinned, onPinChange]);

  /**
   * Get status display info
   */
  const getStatusDisplay = () => {
    switch (bridgeStatus) {
      case 'connected':
        return {
          icon: '🔗',
          text: voiceBridge.status.connected.text,
          color: '#22c55e',
          description: voiceBridge.status.connected.description
        };
      case 'connecting':
        return {
          icon: '⏳',
          text: voiceBridge.status.connecting.text,
          color: '#eab308',
          description: voiceBridge.status.connecting.description
        };
      case 'relay_pending':
        return {
          icon: '📡',
          text: voiceBridge.status.relayPending.text,
          color: '#3b82f6',
          description: voiceBridge.status.relayPending.description
        };
      default:
        return {
          icon: '⚡',
          text: voiceBridge.status.disconnected.text,
          color: '#6b7280',
          description: voiceBridge.status.disconnected.description
        };
    }
  };

  /**
   * Get audience summary
   */
  const getAudienceSummary = () => {
    const parts: string[] = [];

    if (webListeners > 0) {
      // Added 2026-02-23: replaced hardcoded "web user(s)" with voiceBridge constant
      const webTemplate = webListeners > 1 ? voiceBridge.audience.webUsers : voiceBridge.audience.webUser;
      parts.push(formatMessage(webTemplate, { count: webListeners }));
    }

    if (bridgeRelayEnabled && discordListeners > 0) {
      // Added 2026-02-23: replaced hardcoded "Discord user(s)" with voiceBridge constant
      const discordTemplate = discordListeners > 1 ? voiceBridge.audience.discordUsers : voiceBridge.audience.discordUser;
      parts.push(formatMessage(discordTemplate, { count: discordListeners }));
    }

    if (parts.length === 0) {
      return voiceBridge.audience.none;
    }

    return `${voiceBridge.audience.label} ${parts.join(' + ')}`;
  };

  /**
   * Get minimized icon based on mic/mute state
   */
  const getMicIcon = () => {
    if (isMuted) return '🔇';
    return '🔊';
  };

  /**
   * Get relay status icon for minimized view
   */
  const getRelayIcon = () => {
    if (bridgeStatus === 'connected') return '🟢';
    if (bridgeStatus === 'connecting') return '🟡';
    if (bridgeStatus === 'relay_pending') return '🔵';
    return '🔴';
  };

  const status = getStatusDisplay();

  /**
   * Handle pin toggle
   */
  const handlePinToggle = () => {
    setIsPinned(!isPinned);
  };

  /**
   * Handle minimize toggle
   */
  const handleMinimizeToggle = () => {
    setIsMinimized(!isMinimized);
  };

  /**
   * Tooltip content
   */
  const TooltipContent = () => (
    <div style={styles.tooltipContent}>
      <div style={styles.tooltipHeader}>
        <span style={styles.tooltipIcon}>{status.icon}</span>
        <span style={{ ...styles.tooltipTitle, color: status.color }}>{status.text}</span>
      </div>
      <p style={styles.tooltipDescription}>{status.description}</p>
      <p style={styles.tooltipAudience}>{getAudienceSummary()}</p>
      {!bridgeRelayEnabled && (
        <p style={styles.tooltipNote}>
          💡 {voiceBridge.audience.relayComingSoon}
        </p>
      )}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowModal(true);
          setShowTooltip(false);
        }}
        style={styles.learnMoreButton}
        aria-label={voiceBridge.buttons.learnMore.ariaLabel}
      >
        {voiceBridge.buttons.learnMore.label}
      </button>
    </div>
  );

  /**
   * Modal content
   */
  const ModalContent = () => (
    <div 
      style={styles.modalOverlay} 
      onClick={() => setShowModal(false)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="bridge-modal-title"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          setShowModal(false);
        }
      }}
    >
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle} id="bridge-modal-title">
            <span aria-hidden="true">🎙️</span> {BRIDGE_HELP_CONTENT.title}
          </h2>
          <button 
            onClick={() => setShowModal(false)} 
            style={styles.modalClose}
            aria-label={voiceBridge.buttons.close.ariaLabel}
          >
            ×
          </button>
        </div>

        {/* Sections */}
        <div style={styles.modalBody}>
          {BRIDGE_HELP_CONTENT.sections.map((section, index) => (
            <div key={index} style={styles.helpSection}>
              <h3 style={styles.helpHeading}>
                <span style={styles.helpIcon}>{section.icon}</span>
                {section.heading}
              </h3>
              <p style={styles.helpContent}>{section.content}</p>
            </div>
          ))}

          {/* Current Status Box */}
          <div style={styles.statusBox}>
            <h3 style={styles.statusBoxTitle}>{voiceBridge.modal.currentStatus}</h3>
            <div style={styles.statusRow}>
              <span>{status.icon}</span>
              <span style={{ color: status.color }}>{status.text}</span>
            </div>
            <div style={styles.audienceRow}>
              <span>👥</span>
              <span>{getAudienceSummary()}</span>
            </div>
            {!bridgeRelayEnabled && (
              <div style={styles.pendingNote}>
                {voiceBridge.modal.pendingNote}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={styles.modalFooter}>
          <button onClick={() => setShowModal(false)} style={styles.closeButton} aria-label={voiceBridge.buttons.close.ariaLabel}>
            {voiceBridge.buttons.acknowledge.label}
          </button>
        </div>
      </div>
    </div>
  );

  // ==========================================================================
  // FLOATING WIDGET VARIANT
  // ==========================================================================

  if (variant === 'floating') {
    // If pinned, we would render via Portal to sidebar
    // For now, show a note that it would render in sidebar
    if (isPinned) {
      // TODO: Phase 2 - Implement React Portal to sidebar container
      // For now, show in fixed position with "pinned" styling indicator
    }

    // Minimized view: compact tab with icons + participant count
    if (isMinimized) {
      const totalListeners = webListeners + (bridgeRelayEnabled ? discordListeners : 0);
      const countBadge = bridgeRelayEnabled 
        ? `${webListeners}↔${discordListeners}`
        : `${totalListeners}`;

      return (
        <div 
          className={`voice-bridge-info floating minimized ${className}`}
          style={{
            ...styles.floatingMinimized,
            ...(isPinned ? styles.floatingPinned : {})
          }}
        >
          <button
            onClick={handleMinimizeToggle}
            style={styles.minimizedContent}
            aria-label={`Voice bridge: ${status.text}. ${totalListeners} listeners. Click to expand.`}
            title={`${status.text} | ${totalListeners} listeners | Click to expand`}
          >
            <span style={styles.minimizedIcon}>{getMicIcon()}</span>
            <span style={styles.minimizedDivider}>↔</span>
            <span style={styles.minimizedIcon}>{getRelayIcon()}</span>
            {totalListeners > 0 && (
              <span style={styles.minimizedBadge}>{countBadge}</span>
            )}
          </button>
          {showModal && <ModalContent />}
        </div>
      );
    }

    // Expanded floating widget view
    return (
      <div 
        className={`voice-bridge-info floating expanded ${className}`}
        style={{
          ...styles.floatingContainer,
          ...(isPinned ? styles.floatingPinned : {})
        }}
      >
        {/* Header with controls */}
        <div style={styles.floatingHeader}>
          <span style={styles.floatingTitle}>{voiceBridge.floating.title}</span>
          <div style={styles.floatingControls}>
            {/* Minimize button */}
            <button
              onClick={handleMinimizeToggle}
              style={styles.controlButton}
              aria-label={voiceBridge.floating.minimize.ariaLabel}
              title={voiceBridge.floating.minimize.title}
            >
              −
            </button>
            {/* Pin button */}
            <button
              onClick={handlePinToggle}
              style={{
                ...styles.controlButton,
                ...(isPinned ? styles.controlButtonActive : {})
              }}
              aria-label={isPinned ? voiceBridge.floating.pin.ariaLabelUnpin : voiceBridge.floating.pin.ariaLabelPin}
              aria-pressed={isPinned}
              title={isPinned ? voiceBridge.floating.pin.ariaLabelUnpin : voiceBridge.floating.pin.ariaLabelPin}
            >
              📌
            </button>
            {/* Popout button (placeholder for Phase 2) */}
            <button
              onClick={() => {
                // TODO: Phase 2 - Implement popout window
                console.log('[VoiceBridgeInfo] Popout window - coming in Phase 2');
              }}
              style={{
                ...styles.controlButton,
                ...styles.controlButtonDisabled
              }}
              aria-label={voiceBridge.floating.popout.ariaLabel}
              title={voiceBridge.floating.popout.title}
              disabled
            >
              ⧉
            </button>
          </div>
        </div>

        {/* Status */}
        <div style={styles.floatingStatus} role="status" aria-live="polite" aria-atomic="true">
          <span style={styles.statusIcon} aria-hidden="true">{status.icon}</span>
          <span style={{ ...styles.statusText, color: status.color }}>{status.text}</span>
        </div>

        {/* Audience */}
        <div style={styles.floatingAudience} aria-live="polite" aria-atomic="true">
          <span aria-hidden="true">●</span>
          <span>{getAudienceSummary()}</span>
        </div>

        {/* Participants breakdown */}
        {(webListeners > 0 || discordListeners > 0) && (
          <div style={styles.participantsList}>
            {webListeners > 0 && (
              <div style={styles.participantRow}>
                <span style={styles.participantIcon}>🌐</span>
                {/* Added 2026-02-23: replaced hardcoded "Web" with voiceBridge constant */}
                <span>{webListeners} {voiceBridge.audience.webPlatform}</span>
              </div>
            )}
            {bridgeRelayEnabled && discordListeners > 0 && (
              <div style={styles.participantRow}>
                <span style={styles.participantIcon}>🎮</span>
                {/* Added 2026-02-23: replaced hardcoded "Discord" with voiceBridge constant */}
                <span>{discordListeners} {voiceBridge.audience.discordPlatform}</span>
              </div>
            )}
          </div>
        )}

        {/* Onboarding: who-hears-whom summary + privacy note (2026-02-23 Agent Beta D§3.2) */}
        {bridgeRelayEnabled && (
          <div style={styles.onboardingSummary} role="note" aria-label="Voice bridge onboarding information">
            <div style={styles.onboardingRow}>
              <span aria-hidden="true">🌐</span>
              <span aria-hidden="true">→</span>
              <span aria-hidden="true">🎮</span>
              <span style={styles.onboardingText}>{voiceBridge.onboarding.webToDiscord}</span>
            </div>
            <div style={styles.onboardingRow}>
              <span aria-hidden="true">🎮</span>
              <span aria-hidden="true">→</span>
              <span aria-hidden="true">🌐</span>
              <span style={styles.onboardingText}>{voiceBridge.onboarding.discordToWeb}</span>
            </div>
            <div style={styles.onboardingRow}>
              <span aria-hidden="true">🛡️</span>
              <span aria-hidden="true"> </span>
              <span aria-hidden="true"> </span>
              <span style={styles.onboardingText} title="Your real identity stays private — only your character name is shown to other players">Character names protect your privacy across platforms</span>
            </div>
          </div>
        )}

        {/* Relay note if not enabled */}
        {!bridgeRelayEnabled && (
          <div style={styles.relayNote}>
            💡 {voiceBridge.onboarding.relayNote}
          </div>
        )}

        {/* Learn More link */}
        <button
          onClick={() => setShowModal(true)}
          style={styles.floatingLearnMore}
          aria-label={voiceBridge.buttons.learnMore.ariaLabel}
        >
          {voiceBridge.buttons.learnMore.label}
        </button>

        {showModal && <ModalContent />}
      </div>
    );
  }

  // ==========================================================================
  // INLINE VARIANT
  // ==========================================================================

  if (variant === 'inline') {
    return (
      <div className={`voice-bridge-info inline ${className}`} style={styles.inlineContainer}>
        <div style={styles.inlineStatus}>
          <span style={styles.inlineIcon}>{status.icon}</span>
          <span style={{ ...styles.inlineText, color: status.color }}>{status.text}</span>
        </div>
        <div style={styles.inlineDescription}>
          {status.description}
          {!bridgeRelayEnabled && (
            <span style={styles.inlineNote}> · {voiceBridge.inline.relayPending}</span>
          )}
        </div>
        <button
          onClick={() => setShowModal(true)}
          style={styles.inlineHelpButton}
          aria-label={voiceBridge.buttons.learnMore.ariaLabel}
          title={voiceBridge.buttons.learnMore.title}
        >
          ?
        </button>
        {showModal && <ModalContent />}
      </div>
    );
  }

  // ==========================================================================
  // MODAL VARIANT
  // ==========================================================================

  if (variant === 'modal') {
    return (
      <div className={`voice-bridge-info modal ${className}`}>
        <button
          onClick={() => setShowModal(true)}
          style={styles.modalTrigger}
          aria-label={voiceBridge.buttons.help.ariaLabel}
          title={voiceBridge.buttons.help.title}
        >
          <span aria-hidden="true">{status.icon}</span> {children || voiceBridge.floating.modalTriggerFallback}
        </button>
        {showModal && <ModalContent />}
      </div>
    );
  }

  // ==========================================================================
  // TOOLTIP VARIANT (default)
  // ==========================================================================

  const positionStyles = {
    top: { bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: '8px' },
    bottom: { top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '8px' },
    left: { right: '100%', top: '50%', transform: 'translateY(-50%)', marginRight: '8px' },
    right: { left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: '8px' }
  };

  return (
    <div
      className={`voice-bridge-info tooltip ${className}`}
      style={styles.tooltipWrapper}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {children || (
        <span
          style={styles.defaultTrigger}
          title={voiceBridge.floating.defaultTrigger.title}
          role="button"
          tabIndex={0}
          aria-label={voiceBridge.floating.defaultTrigger.title}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowTooltip(!showTooltip);
            }
          }}
          onFocus={() => setShowTooltip(true)}
          onBlur={() => setShowTooltip(false)}
        >
          {status.icon}
        </span>
      )}
      {showTooltip && (
        <div style={{ ...styles.tooltip, ...positionStyles[tooltipPosition] }}>
          <TooltipContent />
        </div>
      )}
      {showModal && <ModalContent />}
    </div>
  );
};

// ============================================================================
// STYLES
// ============================================================================

const styles: Record<string, React.CSSProperties> = {
  // ==========================================================================
  // FLOATING WIDGET STYLES
  // ==========================================================================
  
  floatingContainer: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    width: '320px',
    backgroundColor: 'rgba(30, 30, 30, 0.98)',
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
    zIndex: 1000,
    fontFamily: '"Cinzel", serif'
  },

  floatingPinned: {
    // When pinned, different styling to indicate it's pinned
    borderColor: '#8b5cf6',
    boxShadow: '0 8px 32px rgba(139, 92, 246, 0.2)'
  },

  floatingMinimized: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    backgroundColor: 'rgba(30, 30, 30, 0.98)',
    borderRadius: '20px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
    zIndex: 1000
  },

  minimizedContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#fff'
  },

  minimizedIcon: {
    fontSize: '14px'
  },

  minimizedDivider: {
    fontSize: '10px',
    color: 'rgba(255, 255, 255, 0.4)'
  },

  minimizedBadge: {
    fontSize: '11px',
    backgroundColor: 'rgba(59, 130, 246, 0.3)',
    color: '#93c5fd',
    padding: '2px 6px',
    borderRadius: '10px',
    marginLeft: '4px'
  },

  floatingHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
  },

  floatingTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#fff'
  },

  floatingControls: {
    display: 'flex',
    gap: '4px'
  },

  controlButton: {
    width: '24px',
    height: '24px',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    color: 'rgba(255, 255, 255, 0.6)',
    cursor: 'pointer',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.15s'
  },

  controlButtonActive: {
    backgroundColor: 'rgba(139, 92, 246, 0.3)',
    color: '#a78bfa'
  },

  controlButtonDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed'
  },

  floatingStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 14px'
  },

  statusIcon: {
    fontSize: '18px'
  },

  statusText: {
    fontSize: '14px',
    fontWeight: '500'
  },

  floatingAudience: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '0 14px 8px',
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.6)'
  },

  participantsList: {
    padding: '8px 14px',
    borderTop: '1px solid rgba(255, 255, 255, 0.05)'
  },

  participantRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 0',
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.7)'
  },

  participantIcon: {
    fontSize: '12px'
  },

  relayNote: {
    padding: '8px 14px',
    fontSize: '11px',
    color: '#eab308',
    borderTop: '1px solid rgba(255, 255, 255, 0.05)'
  },

  onboardingSummary: {
    padding: '8px 14px',
    borderTop: '1px solid rgba(255, 255, 255, 0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },

  onboardingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 0.6)'
  },

  onboardingText: {
    marginLeft: '4px',
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 0.5)'
  },

  floatingLearnMore: {
    display: 'block',
    width: '100%',
    padding: '10px 14px',
    background: 'none',
    border: 'none',
    borderTop: '1px solid rgba(255, 255, 255, 0.05)',
    color: '#8b5cf6',
    fontSize: '12px',
    cursor: 'pointer',
    textAlign: 'center'
  },

  // ==========================================================================
  // TOOLTIP STYLES
  // ==========================================================================

  tooltipWrapper: {
    position: 'relative',
    display: 'inline-block'
  },
  defaultTrigger: {
    cursor: 'help',
    fontSize: '16px'
  },
  tooltip: {
    position: 'absolute',
    zIndex: 1000,
    minWidth: '250px'
  },
  tooltipContent: {
    backgroundColor: 'rgba(30, 30, 30, 0.95)',
    borderRadius: '10px',
    padding: '14px',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
  },
  tooltipHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px'
  },
  tooltipIcon: {
    fontSize: '18px'
  },
  tooltipTitle: {
    fontSize: '14px',
    fontWeight: '600'
  },
  tooltipDescription: {
    margin: '0 0 6px',
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.7)',
    lineHeight: 1.4
  },
  tooltipAudience: {
    margin: '0 0 8px',
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 0.5)'
  },
  tooltipNote: {
    margin: '0 0 8px',
    fontSize: '11px',
    color: '#eab308',
    fontStyle: 'italic'
  },
  learnMoreButton: {
    background: 'none',
    border: 'none',
    color: '#8b5cf6',
    fontSize: '11px',
    cursor: 'pointer',
    padding: 0,
    textDecoration: 'underline'
  },

  // ==========================================================================
  // INLINE STYLES
  // ==========================================================================

  inlineContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '8px'
  },
  inlineStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  inlineIcon: {
    fontSize: '16px'
  },
  inlineText: {
    fontSize: '13px',
    fontWeight: '500'
  },
  inlineDescription: {
    flex: 1,
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.6)'
  },
  inlineNote: {
    color: 'rgba(255, 255, 255, 0.4)'
  },
  inlineHelpButton: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    backgroundColor: 'transparent',
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },

  // ==========================================================================
  // MODAL TRIGGER STYLES
  // ==========================================================================

  modalTrigger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    color: 'rgba(255, 255, 255, 0.8)',
    cursor: 'pointer',
    fontSize: '13px'
  },

  // ==========================================================================
  // MODAL STYLES
  // ==========================================================================

  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1001,
    backdropFilter: 'blur(4px)'
  },
  modalContent: {
    backgroundColor: 'rgba(30, 30, 30, 0.98)',
    borderRadius: '16px',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
  },
  modalTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#fff',
    fontFamily: '"Cinzel", serif'
  },
  modalClose: {
    background: 'none',
    border: 'none',
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: '28px',
    cursor: 'pointer',
    lineHeight: 1
  },
  modalBody: {
    padding: '20px 24px'
  },
  helpSection: {
    marginBottom: '20px'
  },
  helpHeading: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: '0 0 8px',
    fontSize: '15px',
    fontWeight: '600',
    color: '#a78bfa'
  },
  helpIcon: {
    fontSize: '16px'
  },
  helpContent: {
    margin: 0,
    fontSize: '13px',
    color: 'rgba(255, 255, 255, 0.7)',
    lineHeight: 1.6,
    whiteSpace: 'pre-line'
  },
  statusBox: {
    backgroundColor: 'rgba(139, 92, 246, 0.1)',
    borderRadius: '10px',
    padding: '16px',
    marginTop: '16px',
    border: '1px solid rgba(139, 92, 246, 0.2)'
  },
  statusBoxTitle: {
    margin: '0 0 12px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#a78bfa'
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    fontSize: '14px'
  },
  audienceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: 'rgba(255, 255, 255, 0.7)'
  },
  pendingNote: {
    marginTop: '12px',
    fontSize: '12px',
    color: '#eab308'
  },
  modalFooter: {
    padding: '16px 24px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  closeButton: {
    padding: '10px 24px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#8b5cf6',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: '"Cinzel", serif'
  }
};

export default VoiceBridgeInfo;
